import React, { useEffect, useState } from 'react';
import _ from 'underscore';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { InteractivePropType } from 'propTypes/common';
import useIsAuthorized from 'hooks/useIsAuthorized';

// eslint-disable-next-line import/no-cycle
import { fulfillInteractive } from './components/helpers';
// eslint-disable-next-line import/no-cycle
import TestWithVariants from './TestWithVariants';

const TestSummary = ({ interactive, initialScore }) => {
	const questionsAmount = interactive.questions.length;
	const [selectedVariants, setSelectedVariants] = useState(
		Array(questionsAmount).fill({})
	);

	const { mode = 'list', questions = [] } = interactive;

	const isAlreadyCompleted = !_.isEmpty(initialScore);
	const [, setScoreDict] = useState(initialScore || {});
	const [score, setScore] = useState(initialScore || null);
	const [resultMetadata, setResultMetadata] = useState(null);
	const [isTestFinished, setIsTestFinished] = useState(false);
	const [isRestarted, setIsRestarted] = useState(false);

	const {
		query: { _id },
		reload,
		replace,
		asPath
	} = useRouter();

	const { isAuthorized } = useIsAuthorized();

	const selectVariant = (variant, index) => {
		setScoreDict((prevValue) => {
			const tempPrevValues = { ...prevValue };
			tempPrevValues[variant.score] = (tempPrevValues[variant.score] || 0) + 1;
			const scoreWithMaxCount =
				tempPrevValues[variant.score] >= (tempPrevValues[score] || 0)
					? variant.score
					: score;
			setScore(scoreWithMaxCount);
			return tempPrevValues;
		});
		setSelectedVariants((prevValues) => {
			const tempPrevValues = [...prevValues];
			tempPrevValues[index] = variant;

			return tempPrevValues;
		});
	};

	const finishTest = () => {
		if (mode === 'slider') {
			setIsTestFinished(true);
		}
	};

	const restartTest = () => {
		if (!isAuthorized) {
			return reload();
		}
		setIsRestarted(true);
		setIsTestFinished(false);
		setScoreDict({});
		setSelectedVariants(Array(questionsAmount).fill({}));
		replace(asPath, undefined, { scroll: true });
		setResultMetadata(null);
	};

	useEffect(() => {
		if (isAlreadyCompleted) {
			const metadata = interactive.results.find(
				(res) => res.forScore === score
			);
			setResultMetadata(metadata);
		}
		window.history.scrollRestoration = 'manual';
	}, []);

	useEffect(() => {
		if (
			mode === 'list' &&
			selectedVariants.filter((item) => !_.isEmpty(item)).length ===
				questionsAmount
		) {
			setIsTestFinished(true);
		}
	}, [selectedVariants]);

	useEffect(() => {
		if (isTestFinished) {
			setIsRestarted(false);
			const metadata = interactive.results.find(
				(res) => res.forScore === score
			);
			setResultMetadata(metadata);
			if (isAuthorized) {
				fulfillInteractive({ score }, interactive._id, _id);
			}
		}
	}, [isTestFinished]);

	const showQuestions =
		(!isAlreadyCompleted && !isTestFinished) ||
		isRestarted ||
		(isTestFinished && mode === 'list');
	const showResult = !_.isEmpty(resultMetadata) && !isRestarted;

	return (
		<TestWithVariants
			{...{
				interactive,
				selectedVariants,
				restartTest,
				selectVariant,
				showQuestions,
				showResult,
				resultMetadata,
				score,
				isTestFinished,
				finishTest,
				questions
			}}
		/>
	);
};

TestSummary.propTypes = {
	interactive: InteractivePropType,
	initialScore: PropTypes.string
};

export default TestSummary;
